import {
  SOURCE_COLOR_COUNT,
  type SourceRegion,
} from "@/components/common/PdfViewer/DeclarationDocumentViewer"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import {
  useEffect,
  useState,
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
} from "react"
import { useDeclarationDocument } from "../../../../api/declarationsApi"
import type { EvidenceAnchor } from "../../../../api/types"

/**
 * Shared building blocks for the studio declaration-review screens (A/B/C),
 * which otherwise each re-implemented these verbatim.
 */

/** The two modalities a declaration can be, as blob types. A workbook wrapped as
 *  `application/pdf` is a lie the PDF viewer used to be the only reader of; the
 *  workbook viewer reads the bytes as a spreadsheet, so the type must be true. */
export const DECLARATION_BLOB_TYPES = {
  pdf: "application/pdf",
  workbook: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
} as const

/**
 * Fetch the declaration document and expose it as a stable object URL (revoked
 * when the bytes change or the screen unmounts). `isPending`/`isError` mirror the
 * underlying request so screens can render their loading/error branch.
 *
 * `kind` is the declaration's MODALITY, and it decides the blob's media type. It
 * used to be hardcoded to PDF, which was invisible while the PDF viewer was the
 * only consumer and wrong the moment a workbook had a viewer of its own — so the
 * caller passes what it already knows the document to be (Studio V2 derives this
 * from the declaration's own file name, `isWorkbookFileName` — see
 * `StudioV2Layout`'s `isWorkbook` for why that beats the extraction read model's
 * `document_kind`, which is absent until a run has completed) rather than this
 * hook assuming.
 *
 * `enabled` (default `true`, so every pre-existing caller is unaffected) gates
 * the byte fetch itself — a caller that does not yet know `kind` (still
 * waiting on the read model that names it) passes `false` so the two requests
 * don't fire concurrently for a document it cannot render yet.
 */
export function useDocumentBlobUrl(
  declId: string,
  kind: keyof typeof DECLARATION_BLOB_TYPES = "pdf",
  enabled: boolean = true,
): {
  url: string | null
  isPending: boolean
  isError: boolean
} {
  const {
    data: docArrayBuffer,
    isPending,
    isError,
  } = useDeclarationDocument(declId, enabled)
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!docArrayBuffer) {
      setUrl(null)
      return
    }
    const blob = new Blob([docArrayBuffer], {
      type: DECLARATION_BLOB_TYPES[kind],
    })
    const objectUrl = URL.createObjectURL(blob)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [docArrayBuffer, kind])
  return { url, isPending, isError }
}

/**
 * Toggle a bar-open flag on ⌘F / Ctrl+F — the studio's one search shortcut,
 * handled at the screen so it works over both the table and the document.
 */
export function useCmdFToggle(
  setOpen: Dispatch<SetStateAction<boolean>>,
): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "f") {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [setOpen])
}

/**
 * Landing on an issue is a place: the document opens on the Original tab with
 * the power bar on, and the walk starts at the issue's first quote. Shared by
 * B and C, which otherwise re-implemented this effect verbatim — only what
 * counts as a "landing" (`landingKey`) differs per screen, so that stays
 * screen-owned and is passed in.
 */
export function useLandingOnIssue(
  landingKey: string | null,
  setBarOpen: Dispatch<SetStateAction<boolean>>,
  setRightMode: (mode: "original") => void,
): void {
  useEffect(() => {
    if (!landingKey) return
    setBarOpen(true)
    setRightMode("original")
  }, [landingKey, setBarOpen, setRightMode])
}

/**
 * Above this row count, let the browser skip layout/paint for off-screen rows
 * (content-visibility) so a 1000+ article pool scrolls/paints like a short one.
 * Rows stay in normal flow — no positioning math — so the layout is unchanged;
 * below the threshold rows are left fully rendered for exact intrinsic sizing.
 *
 * Shared by the two big tables: step 2's article pool and step 3's
 * article × substance grid. Step 3's grid emits SEVERAL rows per article, so its
 * threshold is counted in rows rather than in articles — the cost being paid for
 * is per rendered row.
 */
export const OFFSCREEN_SKIP_THRESHOLD = 60
export const ROW_SKIP_STYLE: CSSProperties = {
  contentVisibility: "auto",
  containIntrinsicSize: "0 44px",
}

/** The minimal statement shape the document tints need. */
export interface SourceStatement {
  id: string
  groupIndex: number
  framework: string
  evidence: EvidenceAnchor[]
}

/**
 * A statement's identity string — the ONE title the app shows for it, wherever
 * it appears (worklist row, card header, document source track). `titleOf` is
 * the flow's run-wide index (`statementTitle` + collision numbering); it answers
 * "" for a legacy group with no staged row, and only then does the positional
 * fallback stand in.
 *
 * The fallback only fires for a legacy group with no staged row (rare), so it
 * is user copy like everything else the reader sees — `translate`, when given,
 * renders it via `MC_DECL_STMT_FALLBACK_LABEL`; omit it only where no
 * translate function is reachable (the fallback then stays in English rather
 * than crash).
 */
export function statementLabel(
  titleOf: (statementId: string) => string,
  s: { id: string; framework: string; groupIndex: number },
  translate?: (
    key: TranslationKeys,
    dynamicValues?: Record<string, string | number>,
  ) => string,
): string {
  const title = titleOf(s.id)
  if (title) return title
  return translate
    ? translate("MC_DECL_STMT_FALLBACK_LABEL", {
        framework: s.framework,
        n: s.groupIndex + 1,
      })
    : `${s.framework} · statement ${s.groupIndex + 1}`
}

/**
 * One evidence anchor → one SourceRegion, in statement-list order, so the
 * document tints carry the same palette colors the statements show on the left.
 *
 * KEYS `${s.id}#0..evidence.length-1` — the FIRST half of a per-statement key
 * space shared with `buildWorkbookSources` (workbookProofs.ts): a statement's
 * cell proofs continue this same space via `evidenceCount` rather than
 * restarting at 0, so a statement carrying both channels never collides two
 * different citations onto one key. Today the two channels are mutually
 * exclusive by document kind, so this function alone never sees a collision —
 * the shared space is what keeps that true even if that ever stops holding.
 */
export function buildSourceRegions(
  statements: SourceStatement[],
  titleOf: (statementId: string) => string,
  translate?: (
    key: TranslationKeys,
    dynamicValues?: Record<string, string | number>,
  ) => string,
): SourceRegion[] {
  return statements.flatMap((s, si) =>
    s.evidence.map((a, ai) => ({
      key: `${s.id}#${ai}`,
      statementId: s.id,
      label: statementLabel(titleOf, s, translate),
      colorIndex: si % SOURCE_COLOR_COUNT,
      page: a.page,
      quote: a.quote,
      // The fallback coordinate, for a page whose text layer the browser cannot read.
      bbox: a.bbox,
      // WHICH copy of `quote` on the page — without it a page repeating the same
      // line resolves to its FIRST copy for every anchor that cites a later one.
      occurrence: a.occurrence ?? undefined,
    })),
  )
}
